// src/context/GlobalStateContext.tsx
import React, {
  createContext,
  useContext,
  useReducer,
  ReactNode,
  useEffect,
  useState,
} from "react";

// Define the type of the global state
interface GlobalState {
  tepnguser: any | null;
}

// Define the actions that can modify the state
type Action =
  | { type: "SET_TEPNGUSER"; payload: any }
  | { type: "RESET"; payload?: string };

// Initial state
const initialState: GlobalState = {
  tepnguser: null,
};

// Read state (if available)
const initialPersistedState = initialState;

// Create context;
const GlobalStateContext = createContext<{
  state: GlobalState;
  dispatch: React.Dispatch<Action>;
}>({
  state: initialState,
  dispatch: () => null,
});

// Reducer to handle actions
const globalStateReducer = (
  state: GlobalState,
  action: Action
): GlobalState => {
  switch (action.type) {
    case "SET_TEPNGUSER":
      return {
        ...state,
        tepnguser: action.payload,
      };

    case "RESET":
      return initialState; // Resets to initial state

    default:
      return state;
  }
};

// Create a provider component
export const GlobalStateProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(
    globalStateReducer,
    initialPersistedState
  );

  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    // Define event listeners for online and offline
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    // Add event listeners
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Cleanup event listeners on component unmount
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  return (
    <GlobalStateContext.Provider value={{ state, dispatch }}>
      <>
        {!isOnline && (
          <div
            className="fixed bottom-0 shadow-lg w-full text-xs"
            style={{
              backgroundColor: "#ff4d4d",
              color: "white",
              padding: "20px",
              textAlign: "center",
              zIndex: "9999",
            }}
          >
            ⚠️ You are now offline. Some features may not be available.{" "}
            <a href="/offline.html" style={{ color: "#000069" }}>
              Click to use Offline Template
            </a>
          </div>
        )}
      </>

      <>{children}</>
    </GlobalStateContext.Provider>
  );
};

export const useGlobalState = () => useContext(GlobalStateContext);
